<template>
  <v-menu v-if="isProfileLoaded"
          data-locator="appbar-profile-menu"
          :open-on-hover="!$vuetify.display.xs"
          location="bottom"
          transition="slide-y-transition"
  >
    <template v-slot:activator="{ props, value }">
      <div class="profile-menu-item" :class="{'profile-menu-item--opened': value}">
        <AppBarMenuItem v-slot="{active}"
                        class="profile-menu-item__button"
                        data-locator="profile-menu-button"
                        :label="profile.firstName"
                        :active="isInProfileRoute"
                        :hover="value"
                        v-bind="props"
                        @click="onProfileItemClick"
        >
          <v-avatar class="profile-menu-item__initials text-caption"
                    data-locator="user-initials"
                    :class="{'profile-menu-item__initials--active': active}"
                    size="24"
          >
            {{ profile.initials }}
          </v-avatar>
        </AppBarMenuItem>
      </div>
    </template>

    <v-list v-model:selected="selectedPath"
            density="compact"
            class="profile-menu-item__menu"
    >
      <v-list-subheader class="px-4 pt-4">
        <h3 data-locator="user-full-name">{{ profile.fullName }}</h3>
        <p class="text-body-1" data-locator="user-email">{{ profile.email }}</p>
      </v-list-subheader>

      <v-list-item v-for="(pathItem) in paths"
                   :value="pathItem.value"
                   :key="pathItem.value"
                   :to="pathItem.to"
                   :data-locator=pathItem.locatorValue
      >
        <v-list-item-title>
          <div class="d-flex align-center column-gap-4">
            <v-badge v-if="isInProfileRoute && pathItem.icon === 'user'"
                     :dot="true"
                     :model-value="!canCurrentUserSign"
                     class="mt-1"
                     color="error"
                     :offset-x="-5"
                     :offset-y="-3"
            >
              <app-icon :icon-name="pathItem.icon"
                        :icon-weight="isInProfileRoute ? 'fas' : 'fal'"
                        :class="{'text-primary': currentRoute.name.includes(pathItem.to.name)}"
                        size="small"
              />
            </v-badge>
            <app-icon v-else
                      :icon-name="pathItem.icon"
                      :icon-weight="currentRoute.name.includes(pathItem.to.name) ? 'far' : 'fal'"
                      :class="{'text-primary': currentRoute.name.includes(pathItem.to.name)}"
                      size="small"
            />

            <span class="font-weight-medium">
              {{ pathItem.text }}
            </span>
          </div>
        </v-list-item-title>
      </v-list-item>
    </v-list>
  </v-menu>
</template>

<script>
import { mapState, mapGetters } from 'vuex'

import AppBarMenuItem from '@/common/app-bar/AppBarMenuItem.vue'
import {
  getCurrentRouteSafe,
  LOGOUT_ROUTE_NAME,
  PROFILE_ROUTE_NAME,
  SUBSCRIPTION_ROUTE_NAME,
} from '@/router'

export default {
  name: 'AppBarProfile',
  components: { AppBarMenuItem },
  props: {
    fullAccessScope: {
      type: Boolean,
      required: true,
    },
  },
  data() {
    return {
      selectedPath: [],
    }
  },
  computed: {
    // We use this one because we are init even before the router is properly finished,
    // since the app can deduce the layout before then and we are at the top level
    currentRoute () {
      return getCurrentRouteSafe()
    },
    ...mapState('user', ['profile']),
    ...mapGetters('user', ['canCurrentUserSign', 'canAccessSubscription']),
    paths () {
      const paths = []
      if (this.fullAccessScope) {
        paths.push({
          value: 'profile',
          text: this.$t('common.app-bar.ProfileMenuItem.profile'),
          icon: 'user',
          to: { name: PROFILE_ROUTE_NAME },
          locatorValue: 'profile-menu-item',
        })
        if (this.canAccessSubscription) {
          paths.push({
            value: 'subscription',
            text: this.$t('common.app-bar.ProfileMenuItem.subscription'),
            icon: 'credit-card',
            to: { name: SUBSCRIPTION_ROUTE_NAME },
            locatorValue: 'subscription-menu-item',
          })
        }
      }
      paths.push({
        value: 'logout',
        text: this.$t('common.app-bar.ProfileMenuItem.logout'),
        icon: 'power-off',
        locatorValue: 'logout-menu-item',
        to: {
          name: LOGOUT_ROUTE_NAME,
          query: {
            isVoluntaryLogOut: true,
            isLogOut: true,
          },
        },
      })
      return paths
    },
    isInProfileRoute () {
      return this.currentRoute.name.includes(PROFILE_ROUTE_NAME)
    },
    isProfileLoaded () {
      return !!this.profile?.firstName && !!this.profile?.initials
    },
  },
  methods: {
    onProfileItemClick () {
      if (!this.isInProfileRoute) {
        this.selectedPath = ['profile']
        this.$router.push({ name: PROFILE_ROUTE_NAME })
      }
    },
  },
}
</script>

<style scoped lang="scss">
.profile-menu-item {
  height: 100%;

  &__button:hover {
    .profile-menu-item__initials {
      color: white;
      background: rgb(var(--v-theme-primary));
    }
  }

  &__initials {
    color: rgb(var(--v-theme-grey-800));
    background: rgb(var(--v-theme-grey-300));

    &--active {
      color: white;
      background: rgb(var(--v-theme-primary));
    }
  }

  &--opened {
    .profile-menu-item__initials {
      color: white;
      background: rgb(var(--v-theme-primary));
    }
  }

  &__menu {
    font-size: 14px;
    font-weight: 500;

    :deep(.v-list-item--variant-text.v-list-item--active) {
      .v-list-item__overlay {
        background-color: rgb(var(--v-theme-primary));
      }
    }

    :deep(.v-list-subheader__text) {
      color: rgb(var(--v-theme-grey-800));
    }
  }
}
</style>
